import { Box, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddIcon } from '@/shared/assets/icons/add-icon';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { TMultiDrawerProps } from '@/shared/components/ui/multi-drawer/multi-drawer.types';
import { Tab } from '@/shared/components/ui/tabs/components/tab/tab';
import { Tabs } from '@/shared/components/ui/tabs/tabs';
import { Trigger } from '@/shared/components/ui/trigger/trigger';

export const MultiDrawer: React.FC<TMultiDrawerProps> = ({
  handlePopoverClose,
  component = 'MenuItem',
  icon,
  disableTabs = false,
  componentLabel,
  componentSize = 'medium',
  defaultTab = 0,
  title,
  headerActions,
  width = '500px',
  tabs,
  trigger,
  tabsFullWidth,
  permission,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<number>(defaultTab);
  const _componentLabel = componentLabel ?? t('common:add');

  useEffect(() => {
    if (defaultTab === activeTab) {
      return;
    }
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const getTrigger = () => {
    if (!trigger) {
      return (
        <Trigger
          component={component}
          icon={icon ?? <AddIcon variant="primary" />}
          buttonSize={componentSize}
          label={_componentLabel}
        />
      );
    }

    return trigger;
  };

  return (
    <DrawerController
      trigger={getTrigger()}
      title={title}
      headerActions={headerActions}
      showDivider={false}
      width={width}
      permission={permission}
    >
      {(handleClose, isOpen) => {
        const _handleClose = () => {
          handleClose();
          handlePopoverClose?.();
        };

        return (
          <Stack height="100%" gap="1rem">
            {!disableTabs && (
              <Tabs value={activeTab} fullWidth={tabsFullWidth}>
                {tabs.map((tab, index) => {
                  return (
                    <Tab
                      disabled={tab.disabled}
                      value={index}
                      key={`tab-${index}`}
                      onClick={() => setActiveTab(index)}
                      label={tab.title}
                      maxLength={40}
                      icon={tab.icon}
                    />
                  );
                })}
              </Tabs>
            )}
            {tabs.map((tab, index) => {
              if (tab.disabled) {
                return null;
              }

              return (
                <Box
                  height="100%"
                  minHeight={0}
                  key={index}
                  hidden={activeTab !== index}
                >
                  {tab.content(_handleClose, isOpen)}
                </Box>
              );
            })}
          </Stack>
        );
      }}
    </DrawerController>
  );
};
